import { useEffect, useState } from 'react';
import { 
    Box, Skeleton, Text, Flex, Icon,
    Button, Menu, MenuButton, MenuList, IconButton,
    Tooltip
} from '@chakra-ui/react';
import { MenuItemRow } from '../../../ui';
import { Tooltip as RechartsTooltip } from 'recharts';
import { RankingItem } from '../../../types/reporting';
import { TbChevronDown, TbDownload, TbInfoCircle } from 'react-icons/tb';
import { exportToCsv } from '../../../utils/csvExport';
import { PIE_DONUT_COLORS } from '../shared/formatters';
import { donutLabelMinPercent, donutMidLabelPoint, foldDonutOther, ReportingDonut } from '../shared/ReportingDonut';
import { useReportingChartFullscreen } from '../shared/reportingChartFullscreen';
import { chartValueFontSize } from '../shared/barValueLabels';

type PieMetric = 'pctOfTotalVolume' | 'pctOfTotalVolumeWoPM' | 'pctOfTotalCount' | 'pctOfTotalProfit';

interface MetricOption {
    value: PieMetric;
    label: string;
    tooltip: string;
}

const METRIC_OPTIONS: MetricOption[] = [
    { 
        value: 'pctOfTotalVolume', 
        label: '% of Total (Volume)', 
        tooltip: 'The proportion a brand contributes to the total order volume' 
    },
    {
        value: 'pctOfTotalVolumeWoPM',
        label: '% of Total (Volume, w/o PM)',
        tooltip: 'The proportion a part type contributes to total volume using base listPrice before any price-match adjustments.',
    },
    { 
        value: 'pctOfTotalCount', 
        label: '% of Total (Count)', 
        tooltip: 'The proportion a brand contributes to the total order count' 
    },
    { 
        value: 'pctOfTotalProfit', 
        label: '% of Total (GP)', 
        tooltip: 'The proportion a brand contributes to the total gross profit' 
    },
];

const PIE_METRICS: PieMetric[] = ['pctOfTotalVolume', 'pctOfTotalVolumeWoPM', 'pctOfTotalCount', 'pctOfTotalProfit'];

function isPieMetric(value: string | undefined): value is PieMetric {
    return !!value && PIE_METRICS.includes(value as PieMetric);
}

interface PartTypePieChartProps {
    data: RankingItem[] | undefined;
    isLoading: boolean;
    height?: number | string;
    hideHeader?: boolean;
    metric?: string;
    labelFormatter?: (name: string) => string;
    onItemClick?: (item: { id?: string; name: string; orderIds?: string[] }) => void;
}

interface PartTypePieSlice {
    id?: string;
    name: string;
    originalName: string;
    value: number;
    orderIds?: string[];
}

// Blue and Purple palette — shared with Invoicing donut (PIE_DONUT_COLORS in formatters.ts)

const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;

const PartTypePieChart = ({ 
    data, 
    isLoading,
    height = 280,
    hideHeader = false,
    metric: controlledMetric,
    labelFormatter,
    onItemClick,
}: PartTypePieChartProps) => {
    const isFullscreen = useReportingChartFullscreen();
    const [internalMetric, setInternalMetric] = useState<PieMetric>(
        isPieMetric(controlledMetric) ? controlledMetric : 'pctOfTotalVolume',
    );

    useEffect(() => {
        if (isPieMetric(controlledMetric)) setInternalMetric(controlledMetric);
    }, [controlledMetric]);

    const metric = hideHeader && isPieMetric(controlledMetric) ? controlledMetric : internalMetric;
    const metricOption = METRIC_OPTIONS.find(o => o.value === metric) || METRIC_OPTIONS[0];

    if (isLoading && (!data || data.length === 0)) {
        return (
            <Flex align="center" justify="center" w="100%" h="100%" p={2}>
                <Skeleton height="100%" width="100%" borderRadius="lg" />
            </Flex>
        );
    }

    if (!data || data.length === 0) {
        const emptyHeight = typeof height === 'number' ? `${height}px` : height;
        return (
            <Box h={emptyHeight} display="flex" alignItems="center" justifyContent="center">
                <Text color="gray.500">No data available</Text>
            </Box>
        );
    }

    const rankedSlices: PartTypePieSlice[] = data
        .map(item => ({
            id: item.id,
            name: labelFormatter ? labelFormatter(item.name) : item.name,
            originalName: item.name,
            value: (item as any)[metric] || 0,
            orderIds: item.orderIds,
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);

    const chartData = foldDonutOther(
        rankedSlices,
        (_tail, otherValue) => ({ name: 'Other', originalName: 'other', value: otherValue }),
        { padTo100: true },
    );

    const slices = chartData.map((entry, index) => ({
        name: entry.name,
        value: entry.value,
        color: PIE_DONUT_COLORS[index % PIE_DONUT_COLORS.length],
        clickable: Boolean(onItemClick) && entry.originalName !== 'other',
    }));

    const renderSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }: any) => {
        if (percent < donutLabelMinPercent(isFullscreen)) return null;
        const { x, y } = donutMidLabelPoint(cx, cy, midAngle, innerRadius, outerRadius);
        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={chartValueFontSize(isFullscreen, true, 11)}
                fontWeight="bold"
            >
                {`${(value || 0).toFixed(1)}%`}
            </text>
        );
    };

    const handleDownload = () => {
        if (!chartData) return;
        exportToCsv(chartData, 'mix_breakdown_data');
    };

    const isFlexHeight = typeof height === 'string';

    return (
        <Box
            h={isFlexHeight ? '100%' : height}
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
            minW={0}
            w="100%"
        >
            {!hideHeader && <Flex justify="space-between" align="center" mb={3} flexShrink={0} bg="gray.50" borderRadius="md" px={3} py={2}>
                <Flex align="center" gap={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">Part Type Breakdown</Text>
                    <IconButton
                        aria-label="Download CSV"
                        icon={<Icon as={TbDownload as React.ElementType} />}
                        size="sm"
                        h="32px"
                        minW="32px"
                        variant="ghost"
                        color="gray.400"
                        _hover={{ color: 'gray.600', bg: 'gray.100' }}
                        onClick={handleDownload}
                    />
                </Flex>
                <Menu placement="bottom-end">
                    <MenuButton
                        as={Button}
                        variant="outline"
                        bg="white"
                        borderColor="gray.300"
                        fontWeight="normal"
                        size="sm"
                        h="32px"
                        px={3}
                        w="200px"
                        textAlign="left"
                        rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                        _hover={{ borderColor: 'gray.400' }}
                        _active={{ bg: 'white' }}
                    >
                        <Text fontSize="xs" noOfLines={1}>
                            {metricOption.label}
                        </Text>
                    </MenuButton>
                    <MenuList p="6px" minW="200px" zIndex={2000} maxH="300px" overflowY="auto">
                        {METRIC_OPTIONS.map(option => {
                            const isSelected = option.value === metric;
                            return (
                                <MenuItemRow
                                    key={option.value}
                                    kind="single"
                                    isSelected={isSelected}
                                    onClick={() => setInternalMetric(option.value)}
                                >
                                    {option.label}
                                    <Tooltip label={option.tooltip} placement="right" hasArrow fontSize="xs">
                                        <Box
                                            as="span"
                                            display="inline-flex"
                                            ml={2}
                                            verticalAlign="middle"
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        >
                                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3} />
                                        </Box>
                                    </Tooltip>
                                </MenuItemRow>
                            );
                        })}
                    </MenuList>
                </Menu>
            </Flex>}

            <ReportingDonut
                slices={slices}
                isFullscreen={isFullscreen}
                label={renderSliceLabel}
                tooltip={(
                    <RechartsTooltip
                        formatter={(value: number) => formatPercent(value)}
                        contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            boxShadow: 'none',
                            fontSize: '12px',
                        }}
                    />
                )}
                onSliceClick={onItemClick
                    ? (_, index) => {
                        const clicked = chartData[index];
                        if (!clicked || clicked.originalName === 'other') return;
                        onItemClick({ id: clicked.id, name: clicked.originalName, orderIds: clicked.orderIds });
                    }
                    : undefined}
            />
        </Box>
    );
};

export default PartTypePieChart;
